import { useEffect, useRef, useState } from "react";
import { Box, useToast } from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import {
  editAVehicleSegment,
  fetchOneVehicleSegment,
} from "../../../actions/management.action";
import Form from "./Form";
import { useNavigate, useParams } from "react-router-dom";

const EditVehicle = () => {
  const [data, setData] = useState({
    segmentName: "",
    description: "",
    carImage: null, // backend expects 'carImage'
    vehicleTypes: "",
    passengerCapacity: 0,
    luggageCapacity: 0,
    fuelTypes: [],
    amenities: [],
    status: "",
    availability: false,
    termsAndConditions: undefined,
  });

  const formRef = useRef(null);
  const toast = useToast();
  const navigator = useNavigate();
  const params = useParams();

  // ---------------- HANDLE SUBMIT ----------------
  const handleSubmit = async (
    event,
    localRentals,
    outStationOneWay,
    outStationRoundTrip,
    airportTransfer,
    premium,
    discount,
    surcharges,
    inclusions,
    exclusions
  ) => {
    try {
      event.preventDefault();
      const formData = new FormData();
    //  console.log("Submitting data:", data);
      // Basic fields
      formData.append("segmentName", data.segmentName || "");
      formData.append("description", data.description || "");
      formData.append("vehicleTypes", data.vehicleTypes || "");
      formData.append("passengerCapacity", data.passengerCapacity || 0);
      formData.append("luggageCapacity", data.luggageCapacity || 0);
      formData.append("status", data.status || "");
      formData.append("availability", data.availability || false);

      // Handle car image
      if (data.carImage instanceof File || data.carImage instanceof Blob) {
        formData.append("carImage", data.carImage);
      }

      // Arrays
      formData.append("fuelTypes", JSON.stringify(data.fuelTypes || []));
      // Send amenities as comma-separated string for backend to split
      formData.append("amenities", typeof data.amenities === 'string' ? data.amenities : (data.amenities || []).join(', '));

      // Pricing info
      let pricing = {
        outstationOneWay: outStationOneWay,
        outstationRoundTrip: outStationRoundTrip,
        airportTransfer,
        localRental: localRentals,
      };
      formData.append("pricingInfo", JSON.stringify(pricing));
      // formData.append("premiumInfo", JSON.stringify(premium));
      // formData.append("discountInfo", JSON.stringify(discount));
      // formData.append("surchargesInfo", JSON.stringify(surcharges));
      formData.append("inclusions", JSON.stringify(inclusions || []));
      formData.append("exclusions", JSON.stringify(exclusions || []));
      formData.append("termsAndConditions", JSON.stringify(data.termsAndConditions || []));
     
      // Submit to backend
      // console.log("Submitting to backend...");

      let res = await editAVehicleSegment(formData, data?._id);

      if (res.success) {
        toast({
          title: "Success",
          description: "Vehicle segment is updated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        navigator("/management/vehicles");
      } else {
        toast({
          title: "Error",
          description: res?.message || "Failed to update vehicle segment",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast({
        title: "Error",
        description: error.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // ---------------- FETCH SINGLE VEHICLE ----------------
  useEffect(() => {
    const id = params.id;
    if (!id) return;

    fetchOneVehicleSegment(id)
      .then((res) => {
        const vehicle = res?.data;
        // console.log("Fetched vehicle data:", vehicle);
        if (vehicle) {
          setData({
            ...vehicle,
            carImage: vehicle.images?.[0] || null, // first image
            amenities: Array.isArray(vehicle.amenities) 
              ? vehicle.amenities.join(', ') 
              : vehicle.amenities || '',
            termsAndConditions: Array.isArray(vehicle.termsAndConditions)
              ? vehicle.termsAndConditions
              : [],
          });
        }
      })
      .catch((err) => {
        console.error("Fetch vehicle error:", err);
        toast({
          title: "Error",
          description: err.message || "Something went wrong while fetching the data",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  }, [params.id, toast]);

  // ---------------- RENDER ----------------
  return (
    <Box px={6} py={8}>
      <BreadCrumbMaker
        items={[
          { link: "/management", title: "Operations Setup" },
          { link: "/management/vehicles", title: "Vehicles" },
          { link: "/management/vehicles/edit", title: "Edit Vehicle", on: true },
        ]}
      />
      <Form
        data={data}
        setData={setData}
        handleSubmit={handleSubmit}
        formRef={formRef}
        isEdit={true}
        onCancel={() => navigator("/management/vehicles")}
      />
    </Box>
  );
};

export default EditVehicle;
